import React, { useState, useEffect } from 'react';
import axios from 'axios';


/**
 * How much data is waiting behind each form. Without it a form that nobody has filled in
 * looks exactly like a form that cannot report here, which is how the Free & Reduced
 * application sat in this list returning nothing while real applications were on file.
 */
const formOptionLabel = (form) => {
  const name = form.instance_name || form.master_form_name || form.form_name || 'Untitled form';
  const n = Number(form.submission_count);
  if (!Number.isFinite(n)) return name;
  if (n === 0) return `${name} — none submitted yet`;
  return `${name} — ${n} submitted`;
};

/**
 * Sort helpers. These live at MODULE scope deliberately.
 *
 * Defining `gradeRank` inside the component in ViewStudents put it in a temporal dead zone -
 * it compiled clean and threw at runtime, blanking the screen. Same shape of code, same
 * placement rule.
 */
const GRADE_ORDER = { Preschool: -3, Daycare: -2, Kindergarten: -1, K: -1, 'Not in School': 99 };

const gradeRank = (grade) => {
  const g = String(grade || '').trim();
  if (g in GRADE_ORDER) return GRADE_ORDER[g];
  const n = parseInt(g, 10);
  return Number.isNaN(n) ? 98 : n;
};

const cmpName = (a, b) =>
  (a.last_name || '').localeCompare(b.last_name || '')
  || (a.first_name || '').localeCompare(b.first_name || '');

/** Students with no submission sort last in either direction, rather than mixing in. */
const cmpDate = (a, b) => {
  const av = a.submitted_at ? Date.parse(a.submitted_at) : null;
  const bv = b.submitted_at ? Date.parse(b.submitted_at) : null;
  if (av === bv) return 0;
  if (av === null) return 1;
  if (bv === null) return -1;
  return av - bv;
};

const cmpParent = (a, b) =>
  (a.parent_last_name || '').localeCompare(b.parent_last_name || '')
  || (a.parent_first_name || '').localeCompare(b.parent_first_name || '');

const cmpLunchDate = (a, b) => {
  const av = a.submitted_at ? Date.parse(a.submitted_at) : null;
  const bv = b.submitted_at ? Date.parse(b.submitted_at) : null;
  if (av === bv) return 0;
  if (av === null) return 1;
  if (bv === null) return -1;
  return av - bv;
};

/** "Aug 27" — enough to scan a daily list without the noise of a full timestamp. */
const shortDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const PDFReports = () => {
  const [forms, setForms] = useState([]);
  const [students, setStudents] = useState([]);
  const [schoolLunchSubmissions, setSchoolLunchSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Selection state
  const [selectedForm, setSelectedForm] = useState('');
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [selectedSubmissions, setSelectedSubmissions] = useState([]);
  const [selectAll, setSelectAll] = useState(false);

  // Filtering state
  const [statusFilter, setStatusFilter] = useState('all');
  const [gradeFilter, setGradeFilter] = useState('all');
  // Default sort is last name: it is what staff expect from a printed roster, and it matches
  // the order the batch export produces, so the stack and the list agree.
  const [sortBy, setSortBy] = useState('last_name');
  const [processedFilter, setProcessedFilter] = useState('all');
  // Sending a form back to a parent for a correction. The note is required: without a
  // reason the parent cannot act on it, so the server rejects an empty one too.
  const [sendBackFor, setSendBackFor] = useState(null);
  const [sendBackNote, setSendBackNote] = useState('');
  const [sendBackBusy, setSendBackBusy] = useState({});
  const [sendBackDone, setSendBackDone] = useState('');
  const [markingProcessed, setMarkingProcessed] = useState({});
  const [enrollmentYearOptions, setEnrollmentYearOptions] = useState([]);
  const [enrollmentSchoolYear, setEnrollmentSchoolYear] = useState('');

  // PDF generation state
  const [generatingPDF, setGeneratingPDF] = useState(false);
  const [pdfData, setPdfData] = useState(null);
  const [batchExporting, setBatchExporting] = useState(false);

  const sanitizeZipFilename = (s) =>
    String(s || 'file')
      .replace(/[^a-z0-9._-]+/gi, '_')
      .replace(/^_+|_+$/g, '')
      .slice(0, 72) || 'file';

  const downloadBlob = (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  /** Single PDF: open in a new tab for File → Print; if pop-up blocked, fall back to download. */
  const openPdfBlobForPrint = (blob, downloadBasename) => {
    const url = window.URL.createObjectURL(blob);
    const win = window.open(url, '_blank', 'noopener,noreferrer');
    if (!win) {
      downloadBlob(blob, `${downloadBasename}_${Date.now()}.pdf`);
      window.URL.revokeObjectURL(url);
      return false;
    }
    setTimeout(() => window.URL.revokeObjectURL(url), 180000);
    return true;
  };

  const loadJSZip = () =>
    new Promise((resolve, reject) => {
      if (window.JSZip) {
        resolve(window.JSZip);
        return;
      }
      const script = document.createElement('script');
      script.src = 'https://cdnjs.cloudflare.com/ajax/libs/jszip/3.10.1/jszip.min.js';
      script.onload = () => resolve(window.JSZip);
      script.onerror = () => reject(new Error('Failed to load ZIP library'));
      document.head.appendChild(script);
    });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);

      const [formsResponse, yearsResponse] = await Promise.all([
        axios.get('/api/reports/pdf/forms'),
        axios.get('/api/reports/enrollment-years')
      ]);
      setForms(formsResponse.data);
      setEnrollmentYearOptions(yearsResponse.data.years || []);
      setEnrollmentSchoolYear((prev) => {
        if (prev) return prev;
        const active = yearsResponse.data.active_enrollment_school_year;
        return active && String(active).trim() ? String(active).trim() : '';
      });

      // Don't load students initially - wait for form selection

    } catch (error) {
      console.error('Error loading PDF report data:', error);
      setError('Failed to load report data');
    } finally {
      setLoading(false);
    }
  };

  // Load students for the selected form
  const loadStudentsForForm = async (formId, yearForQuery) => {
    try {
      let url = '/api/reports/pdf/students';
      if (formId && formId !== 'school-lunch-application') {
        const params = new URLSearchParams();
        params.set('form_instance_id', formId);
        const y = yearForQuery !== undefined ? yearForQuery : enrollmentSchoolYear;
        if (y != null && String(y).trim() !== '') {
          params.set('enrollment_school_year', String(y).trim());
        }
        url += `?${params.toString()}`;
      }
      const studentsResponse = await axios.get(url);
      setStudents(studentsResponse.data);
    } catch (error) {
      console.error('Error loading students for form:', error);
      setError('Failed to load students for selected form');
      setStudents([]);
    }
  };

  // Load school lunch submissions when school lunch form is selected (scoped by enrollment year).
  const loadSchoolLunchSubmissions = async (yearForQuery) => {
    try {
      const y = yearForQuery !== undefined ? yearForQuery : enrollmentSchoolYear;
      const params = new URLSearchParams();
      if (y != null && String(y).trim() !== '') {
        params.set('enrollment_school_year', String(y).trim());
      }
      const qs = params.toString();
      const response = await axios.get(
        `/api/forms/school-lunch-application/submissions${qs ? `?${qs}` : ''}`
      );
      setSchoolLunchSubmissions(response.data);
    } catch (error) {
      console.error('Error loading school lunch submissions:', error);
      setError('Failed to load school lunch submissions');
    }
  };

  /**
   * Mark a submitted form as printed, or clear it.
   *
   * Optimistic, with rollback: the checkbox flips immediately because staff tick these in a
   * run while working through a stack, and waiting on a round trip per row is the difference
   * between usable and not. If the request fails the row reverts and an error shows - a
   * checkbox that stays ticked after a failed save would tell them a form was printed when
   * the record says otherwise, which is worse than being slow.
   *
   * The server's response is authoritative for processed_at and processed_by_name, so the
   * timestamp and attribution shown are the stored ones, not a local guess.
   */
  const handleProcessedToggle = async (student, nextChecked) => {
    const id = student.form_submission_student_id;
    if (!id) return;

    const previous = { processed_at: student.processed_at, processed_by_name: student.processed_by_name };
    setMarkingProcessed((m) => ({ ...m, [id]: true }));
    setStudents((list) =>
      list.map((s) =>
        s.form_submission_student_id === id
          ? { ...s, processed_at: nextChecked ? new Date().toISOString() : null }
          : s
      )
    );

    try {
      const res = await axios.post(`/api/reports/processed/student_form/${id}`, {
        processed: nextChecked
      });
      setStudents((list) =>
        list.map((s) =>
          s.form_submission_student_id === id
            ? { ...s, processed_at: res.data.processed_at, processed_by_name: res.data.processed_by_name }
            : s
        )
      );
      setError('');
    } catch (err) {
      setStudents((list) =>
        list.map((s) => (s.form_submission_student_id === id ? { ...s, ...previous } : s))
      );
      setError(
        err.response?.status === 403
          ? 'You do not have permission to mark forms as printed.'
          : 'Could not save that. The row has been put back the way it was.'
      );
    } finally {
      setMarkingProcessed((m) => {
        const next = { ...m };
        delete next[id];
        return next;
      });
    }
  };

  /** Same contract as handleProcessedToggle, against a household-level lunch submission. */
  const handleLunchProcessedToggle = async (submission, nextChecked) => {
    const id = submission.id;
    if (!id) return;

    const previous = { processed_at: submission.processed_at, processed_by_name: submission.processed_by_name };
    setMarkingProcessed((m) => ({ ...m, [id]: true }));
    setSchoolLunchSubmissions((list) =>
      list.map((x) => (x.id === id ? { ...x, processed_at: nextChecked ? new Date().toISOString() : null } : x))
    );

    try {
      const res = await axios.post(`/api/reports/processed/submission/${id}`, { processed: nextChecked });
      setSchoolLunchSubmissions((list) =>
        list.map((x) =>
          x.id === id ? { ...x, processed_at: res.data.processed_at, processed_by_name: res.data.processed_by_name } : x
        )
      );
      setError('');
    } catch (err) {
      setSchoolLunchSubmissions((list) => list.map((x) => (x.id === id ? { ...x, ...previous } : x)));
      setError(
        err.response?.status === 403
          ? 'You do not have permission to mark forms as printed.'
          : 'Could not save that. The row has been put back the way it was.'
      );
    } finally {
      setMarkingProcessed((m) => {
        const next = { ...m };
        delete next[id];
        return next;
      });
    }
  };

  // Get unique statuses and grades for filtering
  /**
   * Ask the parent for a correction on this form.
   *
   * The row is updated from our own knowledge rather than refetched: the list can be 250
   * rows and a refetch would lose the reader's place mid-pass. On failure the row is put
   * back the way it was, the same convention handleProcessedToggle uses.
   */
  const submitSendBack = async () => {
    const student = sendBackFor;
    if (!student || !student.form_submission_id) return;
    const note = sendBackNote.trim();
    if (!note) return;

    const id = student.form_submission_id;
    setSendBackBusy((m) => ({ ...m, [id]: true }));
    try {
      await axios.post(`/api/form-submissions/request-changes/${id}`, { note });
      setStudents((list) =>
        list.map((s) => (s.form_submission_id === id ? { ...s, changes_requested: true } : s))
      );
      setSendBackFor(null);
      setSendBackNote('');
      setError('');
      setSendBackDone(
        `Sent back to the parent. They can edit that form until they resubmit, and you will get a notification when they do.`
      );
    } catch (err) {
      setError(
        err.response?.status === 403
          ? 'You do not have permission to send forms back.'
          : (err.response?.data?.error || 'Could not send that form back. Nothing was changed.')
      );
    } finally {
      setSendBackBusy((m) => {
        const next = { ...m };
        delete next[id];
        return next;
      });
    }
  };

  const cancelSendBack = async (student) => {
    const id = student.form_submission_id;
    if (!id) return;
    setSendBackBusy((m) => ({ ...m, [id]: true }));
    try {
      await axios.post(`/api/form-submissions/cancel-request-changes/${id}`);
      setStudents((list) =>
        list.map((s) => (s.form_submission_id === id ? { ...s, changes_requested: false } : s))
      );
      setError('');
      setSendBackDone('The request was withdrawn. That form is no longer open to the parent.');
    } catch (err) {
      setError(err.response?.data?.error || 'Could not withdraw that request. Nothing was changed.');
    } finally {
      setSendBackBusy((m) => {
        const next = { ...m };
        delete next[id];
        return next;
      });
    }
  };

  const uniqueStatuses = [...new Set(students.map(s => s.status))];
  const uniqueGrades = [...new Set(students.map(s => s.grade).filter(g => g))].sort();

  /**
   * Sort comparators, keyed by the value the picker sets.
   *
   * Grade sorts numerically with Kindergarten first, not as text - otherwise "10" lands
   * between "1" and "2". `gradeRank` is module scope for the same reason it is in
   * ViewStudents: defining it inside the component put it in a temporal dead zone and blanked
   * the screen at runtime while compiling clean.
   *
   * Date sorts newest first by default, which is what "show me what came in since yesterday"
   * means. Students with no submission sort last in every date order rather than mixing in.
   */
  const SORTS = {
    last_name: (a, b) => cmpName(a, b),
    last_name_desc: (a, b) => cmpName(b, a),
    first_name: (a, b) =>
      (a.first_name || '').localeCompare(b.first_name || '') || cmpName(a, b),
    grade: (a, b) => gradeRank(a.grade) - gradeRank(b.grade) || cmpName(a, b),
    submitted_at: (a, b) => cmpDate(b, a) || cmpName(a, b),
    submitted_at_asc: (a, b) => cmpDate(a, b) || cmpName(a, b)
  };

  // Filter students based on current filters, then sort.
  const filteredStudents = students
    .filter(student => {
      const statusMatch = statusFilter === 'all' || student.status === statusFilter;
      const gradeMatch = gradeFilter === 'all' || student.grade === gradeFilter;
      // "Not yet printed" is the daily working view: hide anything already marked processed.
      const processedMatch =
        processedFilter === 'all'
        || (processedFilter === 'unprocessed' && !student.processed_at)
        || (processedFilter === 'processed' && !!student.processed_at);
      return statusMatch && gradeMatch && processedMatch;
    })
    .sort(SORTS[sortBy] || SORTS.last_name);

  /**
   * Lunch rows, sorted and filtered client-side.
   *
   * The server also accepts ?sort= and defaults to parent last name - that default matters
   * because the batch export shares the same query builder, so the printed stack now comes
   * out in last-name order instead of the parent_id (UUID) order it used to. Sorting here as
   * well keeps the picker instant, with no refetch per sort change.
   */
  const LUNCH_SORTS_UI = {
    last_name: (a, b) => cmpParent(a, b),
    last_name_desc: (a, b) => cmpParent(b, a),
    first_name: (a, b) =>
      (a.parent_first_name || '').localeCompare(b.parent_first_name || '') || cmpParent(a, b),
    grade: (a, b) => cmpParent(a, b),
    submitted_at: (a, b) => cmpLunchDate(b, a) || cmpParent(a, b),
    submitted_at_asc: (a, b) => cmpLunchDate(a, b) || cmpParent(a, b)
  };

  const filteredLunchSubmissions = schoolLunchSubmissions
    .filter((sub) =>
      processedFilter === 'all'
      || (processedFilter === 'unprocessed' && !sub.processed_at)
      || (processedFilter === 'processed' && !!sub.processed_at)
    )
    .sort(LUNCH_SORTS_UI[sortBy] || LUNCH_SORTS_UI.last_name);

  // Check if selected form is school lunch
  const isSchoolLunchForm = selectedForm === 'school-lunch-application';

  const yearScopedPdf = () => !!selectedForm && String(enrollmentSchoolYear || '').trim() !== '';

  const handleEnrollmentYearChange = async (e) => {
    const value = e.target.value;
    setEnrollmentSchoolYear(value);
    setSelectedStudents([]);
    setSelectAll(false);
    if (selectedForm === 'school-lunch-application') {
      await loadSchoolLunchSubmissions(value);
    } else if (selectedForm && selectedForm !== 'school-lunch-application') {
      await loadStudentsForForm(selectedForm, value);
    }
  };

  // Handle select all checkbox
  const handleSelectAll = (checked) => {
    setSelectAll(checked);
    if (isSchoolLunchForm) {
      if (checked) {
        setSelectedSubmissions(schoolLunchSubmissions.map(s => s.id));
      } else {
        setSelectedSubmissions([]);
      }
    } else {
      if (checked) {
        const scoped = yearScopedPdf();
        const list = scoped
          ? filteredStudents
          : filteredStudents.filter((s) => s.has_completed_form);
        setSelectedStudents(list.map((s) => s.id));
      } else {
        setSelectedStudents([]);
      }
    }
  };

  // Handle individual student selection
  const handleStudentSelect = (studentId, checked) => {
    if (checked) {
      setSelectedStudents(prev => [...prev, studentId]);
    } else {
      setSelectedStudents(prev => prev.filter(id => id !== studentId));
      setSelectAll(false);
    }
  };

  // Handle individual submission selection
  const handleSubmissionSelect = (submissionId, checked) => {
    if (checked) {
      setSelectedSubmissions(prev => [...prev, submissionId]);
    } else {
      setSelectedSubmissions(prev => prev.filter(id => id !== submissionId));
      setSelectAll(false);
    }
  };

  // Update select all state when filtered items change
  useEffect(() => {
    if (isSchoolLunchForm) {
      if (schoolLunchSubmissions.length > 0) {
        const allSelected = schoolLunchSubmissions.every(submission => selectedSubmissions.includes(submission.id));
        const someSelected = schoolLunchSubmissions.some(submission => selectedSubmissions.includes(submission.id));
        setSelectAll(allSelected && someSelected);
      }
    } else {
      if (filteredStudents.length > 0) {
        const scoped =
          selectedForm &&
          selectedForm !== 'school-lunch-application' &&
          String(enrollmentSchoolYear || '').trim() !== '';
        const selectableStudents = filteredStudents.filter((student) =>
          scoped || !selectedForm || selectedForm === 'school-lunch-application'
            ? true
            : student.has_completed_form
        );

        if (selectableStudents.length > 0) {
          const allSelected = selectableStudents.every((student) => selectedStudents.includes(student.id));
          const someSelected = selectableStudents.some((student) => selectedStudents.includes(student.id));
          setSelectAll(allSelected && someSelected);
        } else {
          setSelectAll(false);
        }
      }
    }
  }, [
    filteredStudents,
    selectedStudents,
    schoolLunchSubmissions,
    selectedSubmissions,
    isSchoolLunchForm,
    selectedForm,
    enrollmentSchoolYear
  ]);

  const handleSchoolLunchBatchExport = async (format) => {
    try {
      setBatchExporting(true);
      setError('');
      const body = {
        format,
        ...(String(enrollmentSchoolYear || '').trim()
          ? { enrollment_school_year: String(enrollmentSchoolYear).trim() }
          : {})
      };
      const response = await axios.post('/api/forms/school-lunch-application/batch-export', body, {
        responseType: 'blob'
      });
      const mime = format === 'merged' ? 'application/pdf' : 'application/zip';
      const blob = new Blob([response.data], { type: mime });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const stamp = new Date().toISOString().split('T')[0];
      a.download =
        format === 'merged'
          ? `school_lunch_batch_print_${stamp}.pdf`
          : `school_lunch_batch_${stamp}.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (e) {
      if (e.response?.data instanceof Blob) {
        const text = await e.response.data.text();
        try {
          const j = JSON.parse(text);
          setError(j.error || 'Batch export failed');
        } catch {
          setError('Batch export failed');
        }
      } else {
        setError(e.response?.data?.error || e.message || 'Batch export failed');
      }
    } finally {
      setBatchExporting(false);
    }
  };

  // Handle form selection
  /**
   * The Free & Reduced application is filled from its own official template and its
   * submissions live in a different table, so this screen routes it through a separate
   * set of endpoints, keyed off a sentinel id.
   *
   * That is an implementation detail and should not surface as a second entry in the
   * picker: the dropdown used to list the real form instance AND a hardcoded duplicate
   * with nearly the same name, one of which silently returned nothing. Staff now pick
   * the one real form and this maps it onto the sentinel internally.
   */
  const SCHOOL_LUNCH = 'school-lunch-application';
  const schoolLunchFormId = forms.find((f) =>
    /school lunch|free and reduced/i.test(f.instance_name || f.form_name || ''))?.id;
  const asInternalFormId = (formId) => (formId && formId === schoolLunchFormId ? SCHOOL_LUNCH : formId);

  const handleFormSelection = async (rawFormId) => {
    const formId = asInternalFormId(rawFormId);
    setSelectedForm(formId);
    setSelectedStudents([]);
    setSelectedSubmissions([]);
    setSelectAll(false);
    setError('');

    if (formId === 'school-lunch-application') {
      await loadSchoolLunchSubmissions(enrollmentSchoolYear);
    } else if (formId) {
      await loadStudentsForForm(formId);
    } else {
      // No form selected, clear students
      setStudents([]);
    }
  };

  // Generate PDF report
  const handleGeneratePDF = async () => {
    const selectedItems = isSchoolLunchForm ? selectedSubmissions : selectedStudents;

    if (!selectedForm || selectedItems.length === 0) {
      setError(`Please select a form and at least one ${isSchoolLunchForm ? 'family' : 'student'}`);
      return;
    }

    setPdfData(null);
    setError('');

    try {
      setGeneratingPDF(true);

      // —— School lunch: one family → open PDF for print; several → ZIP of selected PDFs ——
      if (isSchoolLunchForm) {
        if (selectedSubmissions.length === 1) {
          const response = await axios.post(
            '/api/forms/school-lunch-application/generate-pdf',
            { submissionId: selectedSubmissions[0] },
            { responseType: 'blob' }
          );
          const blob = new Blob([response.data], { type: 'application/pdf' });
          const opened = openPdfBlobForPrint(blob, 'school_lunch');
          setPdfData({
            message: opened
              ? 'PDF opened in a new tab — use your browser’s Print command. If no tab appeared, a file was downloaded instead (pop-up blocked).'
              : 'PDF was downloaded — open the file and print from your computer.'
          });
          return;
        }

        const JSZip = await loadJSZip();
        const zip = new JSZip();
        let count = 0;
        for (const submissionId of selectedSubmissions) {
          const pdfRes = await axios.get(`/api/forms/school-lunch-application/pdf/${submissionId}`, {
            responseType: 'blob'
          });
          const sub = schoolLunchSubmissions.find((s) => s.id === submissionId);
          const base = `${sanitizeZipFilename(sub?.parent_last_name)}_${sanitizeZipFilename(sub?.parent_first_name)}_${submissionId.slice(0, 8)}`;
          zip.file(`${base}.pdf`, pdfRes.data);
          count++;
        }
        const zipBlob = await zip.generateAsync({ type: 'blob' });
        const timestamp = new Date().toISOString().split('T')[0];
        downloadBlob(zipBlob, `school_lunch_report_${timestamp}.zip`);
        setPdfData({
          record_count: count,
          message: `Downloaded a ZIP with ${count} PDF${count === 1 ? '' : 's'}. Unzip on your computer and open each file to print.`,
          downloaded: true
        });
        return;
      }

      // —— Regular form: one student → open for print; multiple → ZIP from server JSON ——
      const body = {
        form_instance_id: selectedForm,
        student_ids: selectedStudents
      };
      if (String(enrollmentSchoolYear || '').trim() !== '') {
        body.enrollment_school_year = String(enrollmentSchoolYear).trim();
      }

      if (selectedStudents.length === 1) {
        const response = await axios.post('/api/reports/pdf/generate', body, { responseType: 'blob' });
        const blob = new Blob([response.data], { type: 'application/pdf' });
        const opened = openPdfBlobForPrint(blob, 'form_report');
        // This branch's body is the PDF itself, so a degraded report is announced in headers.
        // Without this the page could hand over a substitute listing and say nothing at all.
        const degradedCount = Number(response.headers?.['x-report-degraded'] || 0);
        setPdfData({
          record_count: 1,
          message: opened
            ? 'PDF opened in a new tab — use your browser’s Print command. If no tab appeared, a file was downloaded instead (pop-up blocked).'
            : 'PDF was downloaded — open the file and print from your computer.',
          degraded: degradedCount > 0
            ? [{ student: '',
                 form: forms.find((f) => f.id === selectedForm)?.instance_name || 'this form',
                 reason: response.headers?.['x-report-degraded-reason'] || 'Reason not reported.' }]
            : []
        });
        return;
      }

      const response = await axios.post('/api/reports/pdf/generate', body, { responseType: 'json' });
      const pdfData = response.data;
      if (!pdfData.pdfs || pdfData.pdfs.length === 0) {
        setError('No PDFs were generated. Please check the server logs.');
        return;
      }

      const JSZip = await loadJSZip();
      const zip = new JSZip();
      let validPdfs = 0;
      for (const pdf of pdfData.pdfs) {
        try {
          const base64Regex = /^[A-Za-z0-9+/]*={0,2}$/;
          if (!base64Regex.test(pdf.data)) continue;
          const binaryString = atob(pdf.data);
          const bytes = new Uint8Array(binaryString.length);
          for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i);
          }
          zip.file(pdf.filename, bytes);
          validPdfs++;
        } catch (e) {
          console.error(`ZIP: skip ${pdf.filename}`, e);
        }
      }
      if (validPdfs === 0) {
        setError('No valid PDFs to include in ZIP file.');
        return;
      }
      const zipBlob = await zip.generateAsync({ type: 'blob' });
      const timestamp = new Date().toISOString().split('T')[0];
      downloadBlob(zipBlob, `pdf_reports_${timestamp}.zip`);
      setPdfData({
        record_count: validPdfs,
        message: `Downloaded a ZIP with ${validPdfs} PDF${validPdfs === 1 ? '' : 's'}. Unzip on your computer and open each file to print.`,
        downloaded: true,
        degraded: pdfData.degraded || []
      });
    } catch (error) {
      console.error('Error generating PDF:', error);
      setError(error.response?.data?.error || error.message || 'Failed to generate PDF');
    } finally {
      setGeneratingPDF(false);
    }
  };

  // Print the report
  const handlePrint = () => {
    if (!pdfData) return;

    // Create a printable version of the data
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>PDF Report Summary</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            h1 { color: #2c3e50; }
            table { width: 100%; border-collapse: collapse; margin: 20px 0; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            th { background-color: #f2f2f2; }
            .success { color: #27ae60; font-weight: bold; }
          </style>
        </head>
        <body>
          <h1>PDF Report Summary</h1>
          <p>Report generated on ${new Date().toLocaleString()}</p>
          <p class="success">${pdfData.message}</p>
          <p><strong>Total PDFs Generated:</strong> ${pdfData.record_count}</p>

          <h3>Generated PDF Files:</h3>
          <table>
            <thead>
              <tr>
                <th>Student Name</th>
                <th>Filename</th>
              </tr>
            </thead>
            <tbody>
    `);

    // Add file information
    if (pdfData.files) {
      pdfData.files.forEach((file) => {
        printWindow.document.write(`
          <tr>
            <td>${file.studentName}</td>
            <td>${file.filename}</td>
          </tr>
        `);
      });
    }

    printWindow.document.write(`
            </tbody>
          </table>

          <p><em>Note: Individual PDF files have been generated and downloaded to your device.</em></p>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  if (loading) {
    return <div style={{ padding: '2rem', textAlign: 'center' }}>Loading PDF Reports...</div>;
  }

  return (
    <div style={{ padding: '1rem', maxWidth: '1200px', margin: '0 auto' }}>
      <h2>PDF Reports</h2>

      {error && (
        <div style={{
          background: '#f8d7da',
          color: '#721c24',
          padding: '0.75rem',
          borderRadius: '4px',
          marginBottom: '1rem',
          border: '1px solid #f5c6cb'
        }}>
          {error}
        </div>
      )}

      {/* Form Selection */}
      <div style={{ marginBottom: '2rem' }}>
        <h3>Select Form</h3>
        <select
          // The lunch form is held internally as a sentinel id, so show its real
          // instance as the selected option rather than falling back to "Choose a form".
          value={selectedForm === SCHOOL_LUNCH ? (schoolLunchFormId || '') : selectedForm}
          onChange={(e) => handleFormSelection(e.target.value)}
          style={{
            padding: '0.5rem',
            border: '1px solid #ddd',
            borderRadius: '4px',
            minWidth: '300px'
          }}
        >
          <option value="">Choose a form...</option>
          {forms.map(form => (
            <option key={form.id} value={form.id}>
              {formOptionLabel(form)}
            </option>
          ))}
        </select>
      </div>

      {selectedForm && (
        <div style={{ marginBottom: '1.5rem' }}>
          <label htmlFor="pdf-enrollment-year" style={{ display: 'block', marginBottom: '0.35rem', fontWeight: 600 }}>
            Enrollment school year
          </label>
          <select
            id="pdf-enrollment-year"
            value={enrollmentSchoolYear}
            onChange={handleEnrollmentYearChange}
            style={{ padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px', minWidth: '280px' }}
          >
            {isSchoolLunchForm ? (
              <option value="">Latest application per family (any year)</option>
            ) : (
              <option value="">All years (not scoped — only students with a completed form can be selected)</option>
            )}
            {enrollmentYearOptions.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
          <p style={{ margin: '0.5rem 0 0', fontSize: '0.9rem', color: '#555' }}>
            {isSchoolLunchForm ? (
              <>
                Choose a year to list the <strong>most recent</strong> school lunch application per family for that
                enrollment cycle. Leave unselected to use each family&apos;s latest application across all years. Siblings
                share one household form — filtering by student (via API) returns the same submission for every child in
                that family. Batch export always includes <strong>one PDF per family</strong> for the selected year (or
                latest overall if no year is selected).
              </>
            ) : (
              <>
                Choose a year to list students for that enrollment cycle. You can run PDFs for students who have not
                submitted yet; those receive a &quot;no data submitted yet&quot; page.
              </>
            )}
          </p>
        </div>
      )}

      {/* Student/Family Selection */}
      <div style={{ marginBottom: '2rem' }}>
        <h3>{isSchoolLunchForm ? 'Select Families' : 'Select Students'}</h3>

        {/* Sorting and the printed filter apply to both lists; status/grade are per-student
            only, so they stay hidden for the household-level lunch list. */}
        {!!selectedForm && (
          <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
            {!isSchoolLunchForm && (
            <div>
              <label style={{ marginRight: '0.5rem' }}>Status:</label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                style={{ padding: '0.25rem', border: '1px solid #ddd', borderRadius: '4px' }}
              >
                <option value="all">All Statuses</option>
                {uniqueStatuses.map(status => (
                  <option key={status} value={status}>{status}</option>
                ))}
              </select>
            </div>
            )}

            {!isSchoolLunchForm && (
            <div>
              <label style={{ marginRight: '0.5rem' }}>Grade:</label>
              <select
                value={gradeFilter}
                onChange={(e) => setGradeFilter(e.target.value)}
                style={{ padding: '0.25rem', border: '1px solid #ddd', borderRadius: '4px' }}
              >
                <option value="all">All Grades</option>
                {uniqueGrades.map(grade => (
                  <option key={grade} value={grade}>{grade}</option>
                ))}
              </select>
            </div>
            )}

            <div>
              <label htmlFor="pdf-sort" style={{ marginRight: '0.5rem' }}>Sort by:</label>
              <select
                id="pdf-sort"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                style={{ padding: '0.25rem', border: '1px solid #ddd', borderRadius: '4px' }}
              >
                <option value="last_name">Last name (A–Z)</option>
                <option value="last_name_desc">Last name (Z–A)</option>
                <option value="first_name">First name (A–Z)</option>
                {!isSchoolLunchForm && <option value="grade">Grade</option>}
                <option value="submitted_at">Date submitted (newest first)</option>
                <option value="submitted_at_asc">Date submitted (oldest first)</option>
              </select>
            </div>

            <div>
              <label htmlFor="pdf-processed" style={{ marginRight: '0.5rem' }}>Show:</label>
              <select
                id="pdf-processed"
                value={processedFilter}
                onChange={(e) => setProcessedFilter(e.target.value)}
                style={{ padding: '0.25rem', border: '1px solid #ddd', borderRadius: '4px' }}
              >
                <option value="all">All</option>
                <option value="unprocessed">Not yet printed</option>
                <option value="processed">Already printed</option>
              </select>
            </div>
          </div>
        )}

        {/* Student Table */}
        <div style={{
          border: '1px solid #ddd',
          borderRadius: '4px',
          maxHeight: '400px',
          overflowY: 'auto'
        }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead style={{
              background: '#f8f9fa',
              position: 'sticky',
              top: 0,
              zIndex: 1
            }}>
              <tr>
                <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '1px solid #ddd' }}>
                  <input
                    type="checkbox"
                    checked={selectAll}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                  />
                </th>
                <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '1px solid #ddd' }}>
                  {isSchoolLunchForm ? 'Parent Name' : 'Student Name'}
                </th>
                {isSchoolLunchForm ? (
                  <>
                    <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '1px solid #ddd' }}>
                      Enr. year
                    </th>
                    <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '1px solid #ddd' }}>
                      Students
                    </th>
                    <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '1px solid #ddd' }}>
                      Submitted Date
                    </th>
                    <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '1px solid #ddd' }}>
                      Printed
                    </th>
                  </>
                ) : (
                  <>
                    <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '1px solid #ddd' }}>
                      Grade
                    </th>
                    <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '1px solid #ddd' }}>
                      Enr. year
                    </th>
                    <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '1px solid #ddd' }}>
                      {yearScopedPdf() ? 'Form status' : 'Status'}
                    </th>
                    <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '1px solid #ddd' }}>
                      Submitted
                    </th>
                    <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '1px solid #ddd' }}>
                      Printed
                    </th>
                    {/* Sending a form back belongs next to marking it printed: it is the
                        same row-by-row pass through the day's forms. */}
                    <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '1px solid #ddd' }}>
                      Corrections
                    </th>
                  </>
                )}
              </tr>
            </thead>
            <tbody>
              {isSchoolLunchForm ? (
                // School lunch submissions
                filteredLunchSubmissions.map(submission => (
                  <tr key={submission.id} style={{ borderBottom: '1px solid #eee' }}>
                    <td style={{ padding: '0.75rem' }}>
                      <input
                        type="checkbox"
                        checked={selectedSubmissions.includes(submission.id)}
                        onChange={(e) => handleSubmissionSelect(submission.id, e.target.checked)}
                      />
                    </td>
                    <td style={{ padding: '0.75rem' }}>
                      {/* "Last, First", matching the student list, so a last-name sort reads
                          as sorted rather than looking arbitrary. */}
                      {submission.parent_last_name}
                      {submission.parent_last_name && submission.parent_first_name ? ', ' : ''}
                      {submission.parent_first_name}
                    </td>
                    <td style={{ padding: '0.75rem' }}>
                      {submission.enrollment_school_year || '—'}
                    </td>
                    <td style={{ padding: '0.75rem' }}>
                      {Array.isArray(submission.submission_data?.children) &&
                      submission.submission_data.children.length > 0
                        ? submission.submission_data.children
                            .map((c) => `${c.first_name || ''} ${c.last_name || ''}`.trim())
                            .filter(Boolean)
                            .join(', ')
                        : '—'}
                    </td>
                    <td style={{ padding: '0.75rem' }}>
                      {new Date(submission.submitted_at).toLocaleDateString()}
                    </td>
                    <td style={{ padding: '0.75rem', whiteSpace: 'nowrap' }}>
                      <label style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', cursor: 'pointer', fontWeight: 'normal' }}>
                        <input
                          type="checkbox"
                          checked={!!submission.processed_at}
                          disabled={!!markingProcessed[submission.id]}
                          onChange={(e) => handleLunchProcessedToggle(submission, e.target.checked)}
                        />
                        <span
                          style={{ fontSize: '0.85rem', color: submission.processed_at ? '#2e7d32' : '#777' }}
                          title={
                            submission.processed_at
                              ? `Printed ${shortDate(submission.processed_at)}${submission.processed_by_name ? ` by ${submission.processed_by_name}` : ''}`
                              : 'Not yet marked as printed'
                          }
                        >
                          {submission.processed_at ? shortDate(submission.processed_at) : 'No'}
                        </span>
                      </label>
                    </td>
                  </tr>
                ))
              ) : (
                // Regular students
                filteredStudents.map(student => {
                  const scoped = yearScopedPdf();
                  const canSelect =
                    !selectedForm || selectedForm === 'school-lunch-application' || scoped || student.has_completed_form;
                  return (
                    <tr key={student.id} style={{
                      borderBottom: '1px solid #eee',
                      opacity: canSelect ? 1 : 0.5,
                      backgroundColor: canSelect ? 'transparent' : '#f8f9fa'
                    }}>
                      <td style={{ padding: '0.75rem' }}>
                        <input
                          type="checkbox"
                          checked={selectedStudents.includes(student.id)}
                          onChange={(e) => handleStudentSelect(student.id, e.target.checked)}
                          disabled={!canSelect}
                          title={!canSelect ? 'Student has not completed this form' : ''}
                        />
                      </td>
                      <td style={{ padding: '0.75rem' }}>
                        {/* "Last, First" so a last-name sort reads as sorted. Displaying
                            "First Last" made a correctly ordered list scan as unordered. */}
                        {student.last_name}{student.last_name && student.first_name ? ', ' : ''}{student.first_name}
                      </td>
                      <td style={{ padding: '0.75rem' }}>
                        {student.grade || 'N/A'}
                      </td>
                      <td style={{ padding: '0.75rem' }}>
                        {student.enrollment_school_year || '—'}
                      </td>
                      <td style={{ padding: '0.75rem' }}>
                        {scoped
                          ? (student.has_completed_form ? 'Submitted' : 'No data yet')
                          : student.status}
                      </td>
                      <td style={{ padding: '0.75rem', whiteSpace: 'nowrap' }}>
                        {shortDate(student.submitted_at) || '—'}
                      </td>
                      <td style={{ padding: '0.75rem', whiteSpace: 'nowrap' }}>
                        {student.form_submission_student_id ? (
                          <label style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', cursor: 'pointer', fontWeight: 'normal' }}>
                            <input
                              type="checkbox"
                              checked={!!student.processed_at}
                              disabled={!!markingProcessed[student.form_submission_student_id]}
                              onChange={(e) => handleProcessedToggle(student, e.target.checked)}
                            />
                            <span
                              style={{ fontSize: '0.85rem', color: student.processed_at ? '#2e7d32' : '#777' }}
                              title={
                                student.processed_at
                                  ? `Printed ${shortDate(student.processed_at)}${student.processed_by_name ? ` by ${student.processed_by_name}` : ''}`
                                  : 'Not yet marked as printed'
                              }
                            >
                              {student.processed_at ? shortDate(student.processed_at) : 'No'}
                            </span>
                          </label>
                        ) : (
                          <span style={{ color: '#aaa' }}>—</span>
                        )}
                      </td>
                      <td style={{ padding: '0.75rem', whiteSpace: 'nowrap' }}>
                        {!student.form_submission_id ? (
                          <span style={{ color: '#aaa' }}>—</span>
                        ) : student.changes_requested ? (
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem' }}>
                            <span
                              style={{
                                background: '#fff3cd', border: '1px solid #ffe69c', color: '#8a5a00',
                                borderRadius: '3px', padding: '0.1rem 0.45rem', fontSize: '0.8rem', fontWeight: 600
                              }}
                              title="The parent has been asked for a change and can edit this form until they resubmit."
                            >
                              Sent back
                            </span>
                            <button
                              type="button"
                              onClick={() => cancelSendBack(student)}
                              disabled={!!sendBackBusy[student.form_submission_id]}
                              style={{ background: 'none', border: 'none', padding: 0, color: '#0d6efd', cursor: 'pointer', fontSize: '0.8rem', textDecoration: 'underline' }}
                            >
                              Undo
                            </button>
                          </span>
                        ) : (
                          <button
                            type="button"
                            onClick={() => setSendBackFor(student)}
                            style={{ background: 'none', border: 'none', padding: 0, color: '#0d6efd', cursor: 'pointer', fontSize: '0.85rem', textDecoration: 'underline' }}
                          >
                            Send back
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        <div style={{ marginTop: '1rem' }}>
          {isSchoolLunchForm ? (
            <div>
              <div style={{ color: '#666' }}>
                Showing {schoolLunchSubmissions.length} families
                {selectedSubmissions.length > 0 && ` (${selectedSubmissions.length} selected)`}
              </div>
              <div
                style={{
                  marginTop: '1rem',
                  padding: '1rem',
                  background: '#f0f7ff',
                  borderRadius: '6px',
                  border: '1px solid #c5ddf7'
                }}
              >
                <h4 style={{ margin: '0 0 0.5rem 0' }}>Batch export (entire district)</h4>
                <p style={{ margin: '0 0 0.75rem 0', fontSize: '0.9rem', color: '#444' }}>
                  Uses the <strong>enrollment year</strong> dropdown above (or latest per family if none selected). For
                  many families, prefer ZIP. For a single print job, use the combined PDF — open the file and print once
                  (can be large in memory for 100+ applications; use ZIP if the download fails or times out).
                </p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                  <button
                    type="button"
                    className="btn-primary"
                    disabled={batchExporting}
                    onClick={() => handleSchoolLunchBatchExport('zip')}
                  >
                    {batchExporting ? 'Working…' : 'Download all as ZIP'}
                  </button>
                  <button
                    type="button"
                    className="btn-secondary"
                    disabled={batchExporting}
                    onClick={() => handleSchoolLunchBatchExport('merged')}
                  >
                    Download combined PDF (print once)
                  </button>
                </div>
              </div>
            </div>
          ) : selectedForm && selectedForm !== 'school-lunch-application' ? (
            <div>
              <div style={{ color: '#666' }}>
                Showing {filteredStudents.length} students
                {selectedStudents.length > 0 && ` (${selectedStudents.length} selected)`}
              </div>
              {!yearScopedPdf() && (
                <div style={{ color: '#6c757d', fontSize: '0.9em', marginTop: '0.25rem' }}>
                  Grayed out students have not completed this form and cannot be selected. Choose an enrollment year
                  above to include students who have not submitted yet.
                </div>
              )}
            </div>
          ) : (
            <div style={{ color: '#666' }}>
              Showing {filteredStudents.length} students
              {selectedStudents.length > 0 && ` (${selectedStudents.length} selected)`}
            </div>
          )}
        </div>
      </div>

      {/* Generate PDF Button */}
      <div style={{ marginBottom: '2rem' }}>
        <button
          onClick={handleGeneratePDF}
          disabled={!selectedForm || (isSchoolLunchForm ? selectedSubmissions.length === 0 : selectedStudents.length === 0) || generatingPDF}
          style={{
            background: (!selectedForm || (isSchoolLunchForm ? selectedSubmissions.length === 0 : selectedStudents.length === 0) || generatingPDF) ? '#bdc3c7' : '#27ae60',
            color: 'white',
            border: 'none',
            padding: '0.75rem 2rem',
            borderRadius: '4px',
            cursor: (!selectedForm || (isSchoolLunchForm ? selectedSubmissions.length === 0 : selectedStudents.length === 0) || generatingPDF) ? 'not-allowed' : 'pointer',
            fontSize: '1rem',
            fontWeight: '600'
          }}
        >
          {generatingPDF ? 'Generating PDF...' : 'Run Report'}
        </button>
      </div>

      {/* PDF Results */}
      {pdfData && (pdfData.record_count || pdfData.message) && (
        <div style={{
          border: '1px solid #ddd',
          borderRadius: '4px',
          padding: '1rem',
          background: '#f8f9fa'
        }}>
          {/* Do not claim success when some forms came out as substitutes: an office that
              reads "Successfully" will print the stack without checking. */}
          <h3>
            {pdfData.degraded && pdfData.degraded.length > 0
              ? 'Report Generated — with problems'
              : 'Report Generated Successfully'}
          </h3>
          <p>{pdfData.message}</p>

          {pdfData.degraded && pdfData.degraded.length > 0 && (
            <div style={{
              border: '1px solid #b81c17',
              borderLeft: '5px solid #b81c17',
              borderRadius: '4px',
              padding: '0.75rem 1rem',
              margin: '0.75rem 0',
              background: '#fdf2f2'
            }}>
              <strong style={{ color: '#b81c17' }}>
                {pdfData.degraded.length === 1
                  ? '1 form could not be produced as the real form'
                  : `${pdfData.degraded.length} forms could not be produced as the real form`}
              </strong>
              <p style={{ margin: '0.5rem 0' }}>
                Those PDFs carry a red SUBSTITUTE band at the top of the page. The answers on
                them are complete, but the layout is a plain listing rather than the district’s
                form. Please report this so it can be fixed — do not file them as the official
                form.
              </p>
              <ul style={{ margin: '0.5rem 0 0 1.25rem' }}>
                {pdfData.degraded.map((d, i) => (
                  <li key={i} style={{ marginBottom: '0.25rem' }}>
                    {d.student ? <strong>{d.student}</strong> : null}
                    {d.student ? ' — ' : null}
                    <span style={{ color: '#666' }}>{d.reason}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Manual download links if available */}
          {pdfData.pdfs && pdfData.pdfs.length > 0 && (
            <div style={{ marginTop: '1rem' }}>
              <h4>Manual Downloads:</h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                <button
                  onClick={() => {
                    // Load JSZip if not loaded
                    if (!window.JSZip) {
                      const script = document.createElement('script');
                      script.src = 'https://cdnjs.cloudflare.com/ajax/libs/jszip/3.10.1/jszip.min.js';
                      script.onload = () => createManualZip(pdfData.pdfs);
                      script.onerror = () => alert('Failed to load ZIP library');
                      document.head.appendChild(script);
                    } else {
                      createManualZip(pdfData.pdfs);
                    }

                    function createManualZip(pdfs) {
                      try {
                        const zip = new window.JSZip();
                        let validCount = 0;

                        pdfs.forEach((pdf) => {
                          try {
                            const base64Regex = /^[A-Za-z0-9+/]*={0,2}$/;
                            if (!base64Regex.test(pdf.data)) return;

                            const binaryString = atob(pdf.data);
                            const bytes = new Uint8Array(binaryString.length);
                            for (let i = 0; i < binaryString.length; i++) {
                              bytes[i] = binaryString.charCodeAt(i);
                            }

                            zip.file(pdf.filename, bytes);
                            validCount++;
                          } catch (e) {
                            console.error(`Failed to add ${pdf.filename} to ZIP:`, e);
                          }
                        });

                        if (validCount === 0) {
                          alert('No valid PDFs to download');
                          return;
                        }

                        zip.generateAsync({ type: 'blob' }).then((zipBlob) => {
                          const zipUrl = window.URL.createObjectURL(zipBlob);
                          const link = document.createElement('a');
                          const timestamp = new Date().toISOString().split('T')[0];
                          link.href = zipUrl;
                          link.download = `pdf_reports_manual_${timestamp}.zip`;
                          document.body.appendChild(link);
                          link.click();
                          document.body.removeChild(link);
                          window.URL.revokeObjectURL(zipUrl);
                        });
                      } catch (error) {
                        alert(`Failed to create ZIP: ${error.message}`);
                      }
                    }
                  }}
                  style={{
                    background: '#28a745',
                    color: 'white',
                    border: 'none',
                    padding: '0.5rem 1rem',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    textAlign: 'left',
                    fontSize: '1rem',
                    fontWeight: 'bold'
                  }}
                >
                  📦 Download All as ZIP ({pdfData.pdfs.length} files)
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {sendBackDone && (
        <div
          role="status"
          style={{
            marginTop: '1rem', padding: '0.75rem 1rem', background: '#e8f5e9',
            border: '1px solid #a5d6a7', borderRadius: '6px', color: '#1b5e20', fontSize: '0.9rem'
          }}
        >
          {sendBackDone}{' '}
          <button
            type="button"
            onClick={() => setSendBackDone('')}
            style={{ background: 'none', border: 'none', padding: 0, color: '#1b5e20', cursor: 'pointer', textDecoration: 'underline' }}
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Sending a form back needs a reason typed in, so it is a deliberate dialog rather
          than a one-click action: the note is what the parent actually reads. */}
      {sendBackFor && (
        <div
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex',
            alignItems: 'center', justifyContent: 'center', zIndex: 1100, padding: '1rem'
          }}
          onClick={() => { setSendBackFor(null); setSendBackNote(''); }}
        >
          <div
            role="dialog"
            aria-label="Send this form back to the parent"
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#fff', borderRadius: '8px', padding: '1.25rem',
              width: 'min(32rem, 100%)', boxShadow: '0 12px 32px rgba(0,0,0,0.25)'
            }}
          >
            <h3 style={{ margin: '0 0 0.5rem 0' }}>Send this form back for a correction</h3>
            <p style={{ margin: '0 0 0.75rem 0', fontSize: '0.9rem', color: '#555' }}>
              {sendBackFor.last_name}{sendBackFor.last_name && sendBackFor.first_name ? ', ' : ''}{sendBackFor.first_name}
              {' — '}the parent will see this note in the app and can edit that one form until they
              resubmit, even if parent edits are locked for the district. You will get a
              notification when they have made the change.
            </p>
            <label htmlFor="send-back-note" style={{ display: 'block', fontSize: '0.9rem', fontWeight: 600, marginBottom: '0.25rem' }}>
              What needs to change?
            </label>
            <textarea
              id="send-back-note"
              value={sendBackNote}
              onChange={(e) => setSendBackNote(e.target.value)}
              rows={4}
              placeholder="For example: the emergency contact phone number is missing a digit."
              style={{ width: '100%', padding: '0.5rem', border: '1px solid #ccc', borderRadius: '4px', fontFamily: 'inherit', fontSize: '0.9rem' }}
            />
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '0.875rem' }}>
              <button
                type="button"
                className="btn-secondary"
                onClick={() => { setSendBackFor(null); setSendBackNote(''); }}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn-primary"
                disabled={!sendBackNote.trim() || !!sendBackBusy[sendBackFor.form_submission_id]}
                onClick={submitSendBack}
              >
                {sendBackBusy[sendBackFor.form_submission_id] ? 'Sending…' : 'Send back to parent'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PDFReports;


